import { useEffect, useState } from "react";
import { Star, Quote, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api } from "../lib/site";

function Stars({ value = 5, size = "w-4 h-4", interactive = false, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          disabled={!interactive}
          onClick={interactive ? () => onChange(n) : undefined}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} p-0 border-0 bg-transparent`}
          data-testid={interactive ? `review-star-${n}` : undefined}
        >
          <Star
            className={`${size} ${n <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-zinc-700"}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ customer_name: "", device_repaired: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/reviews");
      setReviews(res.data || []);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.device_repaired || !form.comment) {
      toast.error("Fill in every field to submit a review.");
      return;
    }
    if (form.comment.length < 10) {
      toast.error("Please write at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      setSubmitted(true);
      setForm({ customer_name: "", device_repaired: "", rating: 5, comment: "" });
      toast.success("Thanks — your review is pending approval.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="relative py-24 sm:py-32 border-t border-white/5" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
              [ 05 ] · CUSTOMER REVIEWS
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
              Word from the <span className="text-[#FFCC00]">community</span>.
            </h2>
          </div>
          <Button
            onClick={() => { setShowForm((s) => !s); setSubmitted(false); }}
            variant="outline"
            className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white font-semibold"
            data-testid="reviews-toggle-form"
          >
            {showForm ? "Close form" : "Leave a review"}
          </Button>
        </div>

        {/* Review form */}
        {showForm && !submitted && (
          <form
            onSubmit={submit}
            className="bg-[#121212] border border-zinc-800 rounded-md p-6 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
            data-testid="review-form"
          >
            <div>
              <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Your first name *</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="Jane D."
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
                data-testid="review-name"
              />
            </div>
            <div>
              <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Device repaired *</Label>
              <Input
                value={form.device_repaired}
                onChange={(e) => setForm({ ...form, device_repaired: e.target.value })}
                placeholder="PS5, Xbox, PC..."
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
                data-testid="review-device"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Rating *</Label>
              <Stars
                value={form.rating}
                size="w-7 h-7"
                interactive
                onChange={(n) => setForm({ ...form, rating: n })}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Your review *</Label>
              <Textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Cleaned my PS5 in 2 days, quieter than when I bought it, fair price..."
                rows={5}
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF] resize-none"
                data-testid="review-comment"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-zinc-800">
              <p className="font-mono text-xs text-zinc-500 max-w-md">
                Reviews are moderated. Yours will appear here after Noah approves it.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0055FF] hover:bg-blue-600 text-white rounded-md font-semibold px-8 py-6 text-base"
                data-testid="review-submit"
              >
                {submitting ? "Sending..." : <>Submit review <Send className="ml-2 w-4 h-4" /></>}
              </Button>
            </div>
          </form>
        )}

        {showForm && submitted && (
          <div
            className="bg-[#121212] border border-[#34C759]/40 rounded-md p-8 mb-12 flex items-center gap-4"
            data-testid="review-submitted"
          >
            <CheckCircle2 className="w-8 h-8 text-[#34C759] flex-shrink-0" />
            <div>
              <div className="font-display font-semibold text-lg">Thanks — your review is in the queue.</div>
              <div className="text-zinc-400 text-sm mt-1">
                It'll show up on this page after Noah reviews and approves it.
              </div>
            </div>
          </div>
        )}

        {/* Review list */}
        {reviews.length === 0 ? (
          <div className="bg-[#121212] border border-zinc-800 rounded-md p-12 text-center">
            <Quote className="w-8 h-8 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">
              No approved reviews yet. Be the first — use the button above to leave one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-list">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="tech-card bg-[#121212] border border-zinc-800 rounded-md p-8 flex flex-col"
                data-testid={`review-card-${r.id}`}
              >
                <Stars value={r.rating} size="w-4 h-4" />
                <Quote className="w-6 h-6 text-[#0055FF] opacity-50 mt-6 mb-3" />
                <p className="text-zinc-300 leading-relaxed flex-1">{r.comment}</p>
                <div className="mt-6 pt-4 border-t border-zinc-800 flex items-baseline justify-between">
                  <div className="font-display font-semibold">{r.customer_name}</div>
                  <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                    {r.device_repaired}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
